/**
 * The sender's own copy of a picture they just sent, by its uploaded URL.
 *
 * While a photo uploads, its bubble paints a local object URL of the picked
 * file. Once it has gone, the message carries the stored URL instead, and the
 * bubble used to resolve that from scratch: a signed-link round trip, then the
 * whole picture downloaded again, with a spinner in between — the photo the
 * user was looking at blinked out and came back. A chat app shows you your own
 * file from your own device. So the local URL is handed over to the stored one
 * here, and the attachment resolvers check this before going to the network.
 *
 * Pictures only (photos and video posters): they are small. A video or a
 * document stays on the network path rather than holding 25 MB per file in
 * memory for the rest of the session. Bounded, oldest out first, and each URL
 * evicted is revoked, since this map is the only thing still holding it.
 */

const MAX_COPIES = 40

const copies = new Map<string, string>()

/** Take ownership of `objectUrl` as the local copy of `remoteUrl`. */
export function rememberLocalCopy(remoteUrl: string | null | undefined, objectUrl: string | null | undefined): void {
  if (!remoteUrl || !objectUrl) return
  const previous = copies.get(remoteUrl)
  if (previous && previous !== objectUrl) URL.revokeObjectURL(previous)
  copies.delete(remoteUrl)
  copies.set(remoteUrl, objectUrl)
  while (copies.size > MAX_COPIES) {
    const [oldestKey, oldestUrl] = copies.entries().next().value as [string, string]
    copies.delete(oldestKey)
    URL.revokeObjectURL(oldestUrl)
  }
}

/** The local copy of `remoteUrl`, if this tab sent it. */
export function localCopyOf(remoteUrl: string | null | undefined): string | undefined {
  return remoteUrl ? copies.get(remoteUrl) : undefined
}
